import {
  AsYouType,
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
  type CountryCode,
} from 'libphonenumber-js';

/**
 * Telefone internacional: E.164 no banco, formatado na tela.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  O valor canônico é sempre **E.164** (`+5511999990000`) — sem espaço, sem
 *  parêntese, sem hífen. É o único formato que o `wa.me` aceita e o único que
 *  não é ambíguo entre países. Máscara é coisa de tela: entra e sai daqui.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * O mobile faz máscara à mão (`maskPhoneBR` em `nohraclinic-mobile`), e para o
 * Brasil sozinho aquilo basta. Aqui não: "as bandeiras dos países e as máscaras
 * respectivas" são ~240 formatos que mudam quando operadora e regulador mudam.
 * Escrever isso à mão é assinar um contrato de manutenção que ninguém vai
 * honrar — por isso a dependência.
 */

export const PAIS_PADRAO: CountryCode = 'BR';

export interface Pais {
  codigo: CountryCode;
  /** `+55` — já com o `+`, pronto para exibir. */
  ddi: string;
  nome: string;
  bandeira: string;
}

/**
 * Bandeira como emoji, derivada do ISO-3166-1 alfa-2.
 *
 * Cada letra vira o "regional indicator symbol" correspondente (A = U+1F1E6) e
 * o par forma a bandeira. Sem arquivo de imagem, sem sprite, sem requisição —
 * o que importa numa lista de 240 itens que abre num `<Select>`.
 */
function bandeiraDe(codigo: string): string {
  const BASE = 0x1f1e6;
  const LETRA_A = 'A'.charCodeAt(0);

  return [...codigo.toUpperCase()]
    .map((letra) => String.fromCodePoint(BASE + letra.charCodeAt(0) - LETRA_A))
    .join('');
}

/**
 * Nome do país em português, com o próprio ISO como plano B.
 *
 * `Intl.DisplayNames` existe em todo navegador que o projeto suporta, mas o
 * `try` fica porque ele lança para código de região que a tabela do runtime não
 * conhece — e aí um país sem nome não pode derrubar a lista inteira.
 */
const NOMES = (() => {
  try {
    return new Intl.DisplayNames(['pt-BR'], { type: 'region' });
  } catch {
    return null;
  }
})();

function nomeDe(codigo: CountryCode): string {
  try {
    return NOMES?.of(codigo) ?? codigo;
  } catch {
    return codigo;
  }
}

/**
 * Todos os países, em ordem alfabética de nome — mas com o Brasil primeiro.
 *
 * Ordenar puro deixaria "Brasil" no meio de uma lista de 240 itens, e a
 * esmagadora maioria dos tutores desta clínica é brasileira: o padrão precisa
 * estar ao alcance sem rolar.
 */
export const PAISES: readonly Pais[] = (() => {
  const lista = getCountries()
    .map((codigo) => ({
      codigo,
      ddi: `+${getCountryCallingCode(codigo)}`,
      nome: nomeDe(codigo),
      bandeira: bandeiraDe(codigo),
    }))
    .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));

  const brasil = lista.filter((pais) => pais.codigo === PAIS_PADRAO);
  return [...brasil, ...lista.filter((pais) => pais.codigo !== PAIS_PADRAO)];
})();

/** País de um E.164, para o `<Select>` abrir já na bandeira certa. */
export function paisDoE164(e164: string | null | undefined): CountryCode | null {
  if (!e164) return null;
  return parsePhoneNumberFromString(e164)?.country ?? null;
}

/**
 * Texto digitado + país → E.164, **mesmo incompleto**.
 *
 * ⚠️ Devolver `null` para número pela metade seria um buraco silencioso: o
 * campo emite isto a cada tecla, o formulário trata vazio como "não informado"
 * e um telefone digitado pela metade sumiria no salvamento sem nenhum erro.
 * Aqui, o parcial sai como `+5511999` — que não passa em `telefoneValido`, e é
 * o zod do formulário que recusa, com mensagem.
 *
 * `''` só para campo de fato vazio.
 */
export function paraE164(digitado: string, pais: CountryCode): string {
  const digitos = digitado.replace(/\D/g, '');
  if (!digitos) return '';

  // Colado já em internacional ("+55 11 ..."): o DDI está no próprio texto e
  // prefixar o do país escolhido produziria `+5555119...`.
  if (digitado.trim().startsWith('+')) return `+${digitos}`;

  const numero = parsePhoneNumberFromString(digitado, pais);
  return numero?.number ?? `+${getCountryCallingCode(pais)}${digitos}`;
}

/** Aplica a máscara do país enquanto se digita. */
export function formatarEnquantoDigita(digitado: string, pais: CountryCode): string {
  return new AsYouType(pais).input(digitado);
}

/**
 * E.164 → parte nacional formatada, para preencher o campo ao abrir o form.
 *
 * Devolve só o trecho nacional (`(11) 99999-0000`) porque o DDI já está no
 * `<Select>` ao lado — repeti-lo dentro do input faria o usuário apagá-lo.
 */
export function parteNacional(e164: string | null | undefined): string {
  if (!e164) return '';

  const numero = parsePhoneNumberFromString(e164);
  return numero ? numero.formatNational() : e164;
}

/**
 * Como o telefone aparece em tabela e listagem.
 *
 * Internacional (`+55 11 99999-0000`) e não nacional: numa listagem não há
 * bandeira ao lado dizendo de que país é o número. Valor que não dá para
 * interpretar volta como veio — dado legado não pode sumir da tela.
 */
export function formatarParaExibicao(e164: string | null | undefined): string {
  if (!e164) return '';

  const numero = parsePhoneNumberFromString(e164);
  return numero ? numero.formatInternational() : e164;
}

export function telefoneValido(e164: string | null | undefined): boolean {
  if (!e164) return false;
  return parsePhoneNumberFromString(e164)?.isValid() ?? false;
}

/**
 * Link do WhatsApp com mensagem pronta.
 *
 * ⚠️ `wa.me` **não anexa arquivo** — só abre a conversa com um texto. É por
 * isso que a mensagem carrega o link do documento em vez do PDF: quem espera
 * anexo aqui vai concluir que o envio falhou.
 *
 * O número vai só com dígitos, sem o `+`: é o formato que o `wa.me` exige.
 */
export function linkDoWhatsapp(e164: string, texto: string): string {
  const digitos = e164.replace(/\D/g, '');
  return `https://wa.me/${digitos}?text=${encodeURIComponent(texto)}`;
}
